import React, { useState, useEffect, FC, useRef, useContext } from 'react';

import { SettingContext } from '../context/SettingContext';

import { getAppById } from '../services/apps';
import { getAllLanguages, getDefaultValues, getModels } from '@/services/content';
import { MODEL_INTIAL_OBJECT } from '@/config/settings';
import { AuthContext } from '@/context/AuthContext';

interface Props {
  children: JSX.Element;
  user: any;
}

export const SettingProvider: FC<Props> = ({ children, user }) => {
  const [defaultAppInfo, setDefaultAppInfo] = useState<AppType | null>(null);
  const [modelProvider, setModelProvider] = useState<string>('azure');
  const [modelName, setModelName] = useState<string>('gpt-4');
  const [personality, setPersonality] = useState<string>('Professional');
  const [creativity, setCreativity] = useState<string>('Factual');
  const [role, setRole] = useState<string>('Assistant');
  const [domainExpertise, setDomainExpertise] = useState<string>('General');
  const [writingStyle, setWritingStyle] = useState<string>('Descriptive');
  const [outputLanguage, setOutputLanguage] = useState<string>('English');
  const [showSourceList, setShowSourceList] = useState<boolean>(false);
  const [selectedModel, setSelectedModel] = useState<Model>(MODEL_INTIAL_OBJECT);
  const [allModels, setAllModels] = useState<Model[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [defaultLanguage, setDefaultLanguage] = useState<string>('');
  const [defaultModel, setDefaultModel] = useState<Model>(MODEL_INTIAL_OBJECT);
  const [allLanguages, setAllLanguages] = useState<string[]>([]);
  const [totalImageQuota, setTotalImageQuota] = useState<number | null>(null);
  const [availableImageQuota, setAvailableImageQuota] = useState<number | null>(null);

  const modelUsedForResponse = useRef<Model>(MODEL_INTIAL_OBJECT);
  const languageUsedForResponse = useRef<string>('');
  const settingsIconRef = useRef<HTMLButtonElement | null>(null);
  const { profileInfo } = useContext(AuthContext);
  useEffect(() => {
    const fetchDefaultAppInfo = async () => {
      setDefaultAppInfo(await getAppById(`${import.meta.env.VITE_ELYSIA_APP_ID}`));
    };
    fetchDefaultAppInfo();
    if (defaultAppInfo?.settings) {
      const settings = JSON.parse(defaultAppInfo?.settings);
      setModelProvider(settings.model_provider);
      setModelName(settings.name_of_model);
      setPersonality(settings.personality);
      setCreativity(settings.creativity);
      setRole(settings.role);
      setDomainExpertise(settings.domain_expertise);
      setWritingStyle(settings.writing_style);
      setOutputLanguage(settings.output_language);
      setShowSourceList(settings.show_source_list);
    }
  }, [defaultAppInfo?.settings]);

  useEffect(() => {
    const allModels = async () => {
      const response = await getModels();
      setAllModels(response ?? []);
    };
    const allLanguages = async () => {
      const response = await getAllLanguages();
      setAllLanguages(response);
    };
    allModels();
    allLanguages();
  }, []);

  useEffect(() => {
    if (!allModels.length) return;
    const setDefaults = async () => {
      const response = await getDefaultValues(profileInfo?.userId);
      if (response) {
        setDefaultLanguage(response?.response_language ?? allLanguages[0]);
        const responseModel = allModels.find(
          (model: Model) => model.name_of_model === response?.name_of_model
        );
        if (!responseModel && allModels.length) {
          setDefaultModel(allModels[0]);
        }
        if (responseModel) {
          setDefaultModel(responseModel);
        }
        setAvailableImageQuota(response?.image_monthly_quota_remaining);
        setTotalImageQuota(response?.image_monthly_quota);
      }
    };
    if (profileInfo?.userId) {
      setDefaults();
    }
    //eslint-disable-next-line
  }, [allModels,profileInfo]);

  useEffect(() => {
    if (window.location.pathname.includes('/session')) return;
    setSelectedLanguage(defaultLanguage);
    if (new URLSearchParams(window.location.search).has('app')) return;
    setSelectedModel(defaultModel);
  }, [defaultModel, defaultLanguage]);

  const settingContext: SettingContextStore = {
    modelProvider,
    modelName,
    personality,
    creativity,
    role,
    domainExpertise,
    writingStyle,
    outputLanguage,
    showSourceList,
    selectedModel,
    allModels,
    selectedLanguage,
    defaultLanguage,
    defaultModel,
    allLanguages,
    languageUsedForResponse,
    modelUsedForResponse,
    settingsIconRef,
    availableImageQuota,
    totalImageQuota,
    setModelProvider,
    setModelName,
    setPersonality,
    setCreativity,
    setRole,
    setDomainExpertise,
    setWritingStyle,
    setOutputLanguage,
    setShowSourceList,
    setSelectedModel,
    setAllModels,
    setSelectedLanguage,
    setDefaultLanguage,
    setDefaultModel,
    setAllLanguages,
    setTotalImageQuota,
    setAvailableImageQuota,
  };

  return (
    <SettingContext.Provider value={settingContext}>{user ? children : ''}</SettingContext.Provider>
  );
};
